import { Graphics } from 'pixi.js';
import { Boss } from './boss-base';
import type { PatternRunner } from '@/patterns/emitter';
import type { BulletSystem } from '@/entities/bullets';
import type { TelegraphSink } from '@/render/telegraphs';
import { LENS_PATTERNS, PR, PRISM } from './prism-parts';

export { PRISM } from './prism-parts';

const TAU = Math.PI * 2;
const FACE = TAU / PR.faces;

/** Signed angle difference a − b, wrapped to (−π, π]. */
function wrap(a: number): number {
  let d = a % TAU;
  if (d > Math.PI) d -= TAU;
  if (d <= -Math.PI) d += TAU;
  return d;
}

/**
 * Prism — mirrored faces + one stepping LENS (docs/02 B1). Def, lens
 * patterns and tuning live in prism-parts.ts.
 */
export class PrismBoss extends Boss {
  private lens = 0;
  private next = 0;
  private stepTimer: number = PR.stepTime;
  private refractCd = 0;
  private lastPx = 0;
  private lastPy = 0;
  private time = 0;
  private lensRunners: PatternRunner[] = [];
  private readonly facets = new Graphics();

  constructor(level: number, x: number, y: number, homeX: number, homeY: number) {
    super(PRISM, level, x, y, homeX, homeY);
    this.view.addChild(this.facets);
    this.lensRunners = this.makeRunners(LENS_PATTERNS);
  }

  private get lensAngle(): number {
    return this.lens * FACE;
  }

  /** A point around the prism lines up with the open lens face. */
  private inLens(x: number, y: number): boolean {
    return Math.abs(wrap(Math.atan2(y - this.y, x - this.x) - this.lensAngle)) <= PR.lensHalf;
  }

  /** Damage only lands through the lens — vulnerable while the ship faces it. */
  override get isVulnerable(): boolean {
    return super.isVulnerable && this.inLens(this.lastPx, this.lastPy);
  }

  protected override get telegraphing(): boolean {
    return super.telegraphing || this.lensRunners.some((r) => r.warning);
  }

  override update(
    dt: number, px: number, py: number, bullets: BulletSystem, telegraphs?: TelegraphSink,
  ): void {
    this.lastPx = px;
    this.lastPy = py;
    super.update(dt, px, py, bullets, telegraphs);
    if (this.dead) return;
    this.time += dt;
    this.refractCd -= dt;
    this.stepLens(dt, px, py);
    // The lens only fires while it looks at the ship
    if (this.inLens(px, py)) {
      const ox = this.x + Math.cos(this.lensAngle) * this.def.radius;
      const oy = this.y + Math.sin(this.lensAngle) * this.def.radius;
      for (const r of this.lensRunners) r.update(dt, ox, oy, px, py, bullets, telegraphs);
    }
  }

  /** The lens steps one face toward the ship; the next face flickers first. */
  private stepLens(dt: number, px: number, py: number): void {
    const prev = this.stepTimer;
    this.stepTimer -= dt;
    if (prev > PR.flicker && this.stepTimer <= PR.flicker) {
      const toShip = wrap(Math.atan2(py - this.y, px - this.x) - this.lensAngle);
      const dir = Math.abs(toShip) <= PR.lensHalf ? 0 : Math.sign(toShip);
      this.next = (this.lens + dir + PR.faces) % PR.faces;
    }
    if (this.stepTimer <= 0) {
      this.stepTimer = PR.stepTime;
      this.lens = this.next;
    }
  }

  /**
   * Lens hits wound; mirror hits refract into two stray shards. Each impact
   * reports its own result (numbers follow the impact angle).
   */
  override absorbBullets(bullets: BulletSystem): { hits: number; dealt: number } {
    const before = this.hp;
    const hits = bullets.collideCircle(true, this.x, this.y, this.def.radius, (dmg, bx, by) => {
      if (this.inLens(bx, by)) return this.landHit(dmg);
      this.refract(bullets, bx, by);
      return 0;
    });
    return { hits, dealt: before - Math.max(0, this.hp) };
  }

  private refract(bullets: BulletSystem, bx: number, by: number): void {
    this.hitFlash = 0.08;
    if (this.refractCd > 0) return;
    this.refractCd = PR.refractCd;
    const out = Math.atan2(by - this.y, bx - this.x);
    for (let k = 0; k < 2; k++) {
      const a = out + (k === 0 ? -PR.refractSpread : PR.refractSpread);
      bullets.spawn({
        x: bx, y: by, dirX: Math.cos(a), dirY: Math.sin(a),
        speed: PR.refractSpeed * this.bulletSpeedMul, life: PR.refractLife,
        radius: PR.refractRadius, damage: 1,
        friendly: false, texKey: 'bullet/enemy/shard', rotates: false,
      });
    }
  }

  /** Blasts follow the lens rule too (X5). */
  override applyAoe(x: number, y: number, radius: number, damage: number): number {
    if (!this.hitByAoe(x, y, radius) || !this.inLens(x, y)) return 0;
    return super.applyAoe(x, y, radius, damage);
  }

  override render(alpha: number): void {
    super.render(alpha);
    const g = this.facets;
    g.clear();
    const R = PR.overlayR;
    const flick = this.stepTimer <= PR.flicker && this.next !== this.lens &&
      Math.sin(this.time * 40) > 0;
    for (let k = 0; k < PR.faces; k++) {
      // Face k is centred on angle k·60°, between two hexagon vertices
      const a0 = k * FACE - FACE / 2;
      const a1 = k * FACE + FACE / 2;
      const x0 = Math.cos(a0) * R;
      const y0 = Math.sin(a0) * R;
      const x1 = Math.cos(a1) * R;
      const y1 = Math.sin(a1) * R;
      if (k === this.lens) {
        g.poly([0, 0, x0, y0, x1, y1]).fill({ color: PR.lensDark, alpha: 0.55 });
        g.moveTo(x0, y0).lineTo(x1, y1).stroke({ color: PR.lens, width: 5, alpha: 0.95 });
      } else {
        const blink = flick && k === this.next;
        g.moveTo(x0, y0).lineTo(x1, y1).stroke({
          color: blink ? PR.lens : PR.mirror, width: blink ? 5 : 3.5, alpha: blink ? 0.95 : 0.7,
        });
      }
    }
  }
}
